/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */
/** @jsx jsx */

import { css, jsx } from '@emotion/react'

import {
  EuiPanel,
  EuiSwitch,
  EuiFlexGroup,
  EuiFlexItem,
  EuiTextColor,
  EuiText,
  htmlIdGenerator,
  useEuiTheme,
} from '@elastic/eui'

import type { ReactNode, FunctionComponent } from 'react'

type Props = {
  children?: ReactNode
  isChecked: boolean
  isDisabled: boolean
  title: ReactNode
  description: ReactNode
  testId: string
  ariaLabel?: string
  onSwitchToggled: () => void
}

const RolePanel: FunctionComponent<Props> = ({
  isChecked,
  isDisabled,
  title,
  description,
  testId,
  ariaLabel,
  onSwitchToggled,
  children,
}) => {
  const { euiTheme } = useEuiTheme()

  const defaultFlexGroupStyle = css({
    margin: `-${euiTheme.size.base}!important`,
    borderRadius: euiTheme.border.radius.medium,
    padding: euiTheme.size.base,
    backgroundColor: 'transparent',
    gap: euiTheme.size.base,
  })

  const selectedFlexGroupStyle = css({
    backgroundColor: euiTheme.colors.backgroundBaseSubdued,
    border: 0,
  })

  const selectedRoleStyle = css({
    borderColor: euiTheme.colors.primary,
  })

  const headerId = htmlIdGenerator()()

  return (
    <EuiPanel
      disabled={isDisabled}
      hasShadow={false}
      hasBorder={true}
      css={isChecked && selectedRoleStyle}
    >
      <EuiFlexGroup
        gutterSize='none'
        css={[defaultFlexGroupStyle, isChecked && selectedFlexGroupStyle]}
        justifyContent='spaceBetween'
      >
        <EuiFlexItem grow={true}>
          <EuiText size='m'>
            <h5 id={headerId}>{title}</h5>

            <EuiTextColor color='subdued'>
              <EuiText size='s'>{description}</EuiText>
            </EuiTextColor>
          </EuiText>
        </EuiFlexItem>

        <EuiFlexItem grow={false} css={css({ alignItems: `start`, alignSelf: `center` })}>
          <EuiSwitch
            disabled={isDisabled}
            checked={isChecked}
            onChange={() => {
              onSwitchToggled()
            }}
            label=''
            showLabel={false}
            data-test-id={testId}
            aria-label={ariaLabel}
            aria-labelledby={headerId}
          />
        </EuiFlexItem>
      </EuiFlexGroup>
      {children}
    </EuiPanel>
  )
}

export default RolePanel
